import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Collapse, Select, Descriptions, Tooltip } from 'antd';
import renderFileIcon from '@/pages/fileIcon';
import { Close, InfoFilled, WarmingCircle } from 'patternx-svg';
import { BimViewer, ToolbarMenuId } from '@pattern-x/gemini-viewer-threejs';
import UnFinMention from '../unfinishedMention';
import {
  getComponentInfo,
  getModelTransferStatusByModelIds,
  getElementDetailByModelIdApi,
  getComponentBase,
  getComponentDownUrl,
} from '@/services/source';
import { deepfind } from '../../utils';
import styles from './index.module.less';
import ChangeList from './components/changeList';
import DwgView from './components/dwg';
import { useCallbackFn } from '@/hooks';
import { EventEmitter } from 'events';

interface props {
  data: ELEMENT.Component | undefined;
  onClose: Function;
  tags: any[];
  appOption: any[];
}

const modelConfig = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  edges: true,
  visible: true,
};

const dwgEventBus = new EventEmitter();

const ViewElement = (props: props) => {
  const { data, onClose, tags, appOption } = props;
  const multiTagFileTypes = ['skp', 'gltf', 'glb', 'max', 'zip'];

  const generateFileIcon = () => {
    const fileSuffix = data?.sourceFileSuffix || '';
    return renderFileIcon(fileSuffix, 32);
  };

  const handleClose = () => {
    onClose();
  };

  const bimViewer: any = useRef();
  const [projectInfo, setProjectInfo] = useState<ELEMENT.projectInfo>();
  const [elementInfo, setElementInfo] = useState<ELEMENT.elementInfo>();
  const [elementDetail, setElementDetail] = useState<ELEMENT.elementListItem[]>([]);
  const [typeList, setTypeList] = useState<{ label: string; value: string }[]>([]);
  const [typeValue, setTypeValue] = useState<string>('');
  const [dwgFiles, setDwgFiles] = useState<ELEMENT.modelFiles[]>([]);
  const [activeData, setActiveData] = useState<any>({
    storageFileId: 'model',
  });
  const [viewType, setViewType] = useState('3d');

  /**
   * 获取构件信息
   */
  const getElementInfo = async () => {
    const res = await getComponentInfo(data?.componentId || '');
    if (res?.success) {
      setElementInfo(res?.data?.details);
    }
  };

  const getElementDetail = async (name?: string) => {
    if (name !== undefined) {
      setTypeValue(name);
    }
    const res = await getElementDetailByModelIdApi({
      modelId: data?.modelId || '',
      name,
      signature: data?.signature || '',
    });
    if (res?.success) {
      setElementDetail(res?.data?.componentElementList || []);
      const nameList = res?.data?.nameList || [];
      setTypeList(nameList.map((item: string) => ({ value: item, label: item })));
      if (name === undefined) {
        setTypeValue(nameList?.[0]);
      }
    }
  };

  const loadBim = () => {
    bimViewer.current = new BimViewer({
      containerId: 'myCanvas',
      enableAxisGizmo: false,
      enableContextMenu: false,
      enableBottomBar: false,
    });

    const toolbar = bimViewer.current.toolbar;
    //隐藏bim的 toolbar里的 bim树/正教视图/测量/剖切
    toolbar.updateMenu(ToolbarMenuId.BimTree, { visible: false });
    toolbar.updateMenu(ToolbarMenuId.OrthoMode, { visible: false });
    toolbar.updateMenu(ToolbarMenuId.Measure, { visible: false });
    toolbar.updateMenu(ToolbarMenuId.Section, { visible: false });

    bimViewer.current?.setEnvironment('/static/photo_studio_01_1k.hdr');
    let counter = 0; // to indicate how many models are loading
    projectInfo?.models?.forEach((modelCfg) => {
      if (modelCfg.visible === false) {
        // visible is true by default
        return; // only load visible ones
      }
      counter++;
      bimViewer.current.loadModel(modelCfg).then(
        () => {
          counter--;
          if (counter === 0) {
            if (bimViewer.current.has2dModel && !bimViewer.current.has3dModel) {
              bimViewer.current.active2dMode();
            }
          }
        },
        (err: any) => {
          console.log(err);
        },
      );
    });
  };

  /**
   * 获取模型信息
   */
  const getModels = async () => {
    const result = await getComponentBase(data?.modelId || '');
    const res = result?.data?.details || {
      id: 'technical_school_normal',
      name: 'technical_school_normal',
      thumbnail: '/projects/technical_school_normal/thumbnail.png',
      transferStatus: 'success',
      modelFiles: [
        {
          id: 'id1',
          modelId: 'xxx',
          name: '3楼结构模型',
          storageFileId: 'zzz',
          gltfStorageFileId: '111',
          geometryStorageFileId: '',
          thumbnail: '',
          transferStatus: 'success',
          src: '',
        },
      ],
    };
    const modelFiles = res?.modelFiles || [];
    for (let i = 0; i < modelFiles.length; i++) {
      if (modelFiles[i]?.onlyImage === 1) {
        modelFiles[i].src = modelFiles[i]?.thumbnail;
        modelFiles[i].fileFormat = 'jpg';
        continue;
      }
      if (!modelFiles[i]?.gltfStorageFileId) {
        continue;
      }
      const urlRes = await getComponentDownUrl(
        modelFiles[i]?.gltfStorageFileId || '',
        modelFiles[i]?.name || '',
      );
      if (urlRes?.data?.details?.downloadUrl) {
        modelFiles[i].src = urlRes?.data?.details?.downloadUrl;
      } else {
        // TODO 模拟数据
        if (i === 0) {
          modelFiles[i].src = 'https://minio.api.patternx.com/public-test/bimg/3楼结构模型.gltf';
        }
      }
    }
    setDwgFiles(
      modelFiles?.filter((f: ELEMENT.modelFiles) => f.outputType?.toLowerCase() === 'dxf'),
    );
    const models = modelFiles?.map((model: ELEMENT.modelFiles) => {
      return {
        name: model?.name,
        src: model?.src,
        onlyImage: model?.onlyImage,
        thumbnail: model?.thumbnail,
        fileFormat: model?.fileFormat,
        ...modelConfig,
      };
    });

    const project: ELEMENT.projectInfo = {
      id: res?.id,
      name: res?.name,
      thumbnail: res?.thumbnail,
      models: models,
    };
    setProjectInfo(project);
  };

  const [isMention, setIsMention] = useState(false);

  const getIsAllSuccess = async () => {
    if (data?.modelId) {
      const res = await getModelTransferStatusByModelIds([data?.modelId]);
      if (res?.success) {
        setIsMention(!res?.data?.details?.[0].isAllTransferSuccess || false);
      }
    }
  };

  useEffect(() => {
    if (data) {
      getModels();
      getElementDetail();
      getElementInfo();
      getIsAllSuccess();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    return () => {
      bimViewer.current?.destroy();
      handleClose();
    };
  }, []);

  useEffect(() => {
    if (projectInfo) {
      loadBim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectInfo]);

  const getName = (ele: any, labels: any[]) => {
    if (ele?.pid) {
      const activeElement = deepfind(tags, 'id', ele.pid);
      if (activeElement?.name) {
        labels.unshift(activeElement.name);
      }
      getName(activeElement, labels);
    }
  };

  const tagRender = () => {
    if (elementInfo?.tagIdList && elementInfo?.tagIdList.length) {
      const mapResult: any[] = [];
      elementInfo.tagIdList.forEach((curTag: any) => {
        const mapLabels: any[] = [];
        const ele = deepfind(tags, 'id', curTag);
        if (ele) {
          mapLabels.unshift(ele.name);
          if (ele?.pid) {
            getName(ele, mapLabels);
          }
        }
        mapResult.push(mapLabels.join('/'));
      });
      return mapResult.join(',');
    }
    return '';
  };

  const renderApps = useMemo(() => {
    type app = {
      name: string;
      code: string;
      invalid: boolean;
    };
    const apps: app[] = [];
    (elementInfo?.appIdList || [])?.forEach((app) => {
      const appName = appOption?.find((o) => o.code === app)?.name;
      const invalid = elementInfo?.invalidLinkDTOList?.find(
        (dto) => dto.appName === app && dto.invalid,
      );
      apps.push({
        name: appName,
        code: app,
        invalid: !!invalid,
      });
    });
    return {
      apps,
      invalidApps: apps?.filter((app) => app.invalid)?.map((app) => app.name) || [],
    };
  }, [elementInfo, appOption]);

  const onItemClick = useCallbackFn((item?: ELEMENT.modelFiles) => {
    if (item) {
      if (activeData.storageFileId !== item.storageFileId) {
        setActiveData(item);
        setViewType('2d');
        dwgEventBus.emit('destroyDxf');
      }
    } else {
      if (activeData.storageFileId !== 'model') {
        setActiveData({ storageFileId: 'model' });
        setViewType('3d');
      }
    }
  });

  const fileSuffix = useMemo(() => {
    if (elementInfo?.category === 'gmodeler') {
      return 'glb';
    }
    if (elementInfo?.sourceFileSuffix === 'zip') {
      return 'max';
    }
    return elementInfo?.sourceFileSuffix || '';
  }, [elementInfo]);

  return (
    <div className={styles.modalLayout}>
      <div className={styles.header}>
        <div className={styles.center}>
          <div className={styles.title}>
            <span>{generateFileIcon()}</span>
            {data?.name}
          </div>
        </div>
        <Close className={styles.close} onClick={handleClose} />
      </div>
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.center}>
            <div className="project-panel">
              <div id="myCanvas" className="myCanvas" />
            </div>
            <ChangeList onItemClick={onItemClick} datas={dwgFiles} activeData={activeData} />
            {viewType === '2d' && (
              <DwgView
                data={activeData}
                fileId={activeData.storageFileId}
                dwgEventBus={dwgEventBus}
              />
            )}
          </div>
          <div className={styles.right}>
            <Collapse defaultActiveKey={['tag', 'detail']} ghost>
              <Collapse.Panel header="构件标签" key="tag">
                <ul className={styles.basic_info}>
                  <li className={`${styles.basic_info_item} ${styles.flexStart}`}>
                    <label className={styles.label}>分类</label>
                    <span className={`${styles.value} ${styles.value2}`}>{tagRender()}</span>
                  </li>
                  <li className={`${styles.basic_info_item} ${styles.mat12}`}>
                    <label className={styles.label}>应用</label>
                    <span className={styles.value}>
                      {renderApps.apps?.map((app) => (
                        <span
                          className={`${styles.valueItem} ${
                            app.invalid ? styles.warningValueItem : ''
                          }`}
                          key={app.code}
                        >
                          {app.name}
                        </span>
                      ))}
                      {renderApps.invalidApps?.length > 0 ? (
                        <Tooltip
                          title={`该构件不符合${renderApps.invalidApps?.join(
                            '、',
                          )}应用构件标准，应用端无法显示该构件`}
                          overlayClassName={styles.appWarningTooltip}
                        >
                          <WarmingCircle className={styles.warningIcon} />
                        </Tooltip>
                      ) : null}
                    </span>
                  </li>
                  <li className={`${styles.basic_info_item} ${styles.mat12}`}>
                    <label className={styles.label}>文件类型</label>
                    <span className={`${styles.value}`}>{fileSuffix}</span>
                  </li>
                </ul>
              </Collapse.Panel>

              {!multiTagFileTypes.includes(data?.sourceFileSuffix || '') ||
              data?.category === 'gmodeler' ? (
                <Collapse.Panel header="构件详情" key="detail">
                  <UnFinMention // 构件详情
                    visible={isMention}
                    text="对应文件的属性信息还在处理中，请稍等。"
                    icon={<InfoFilled color="#2e90fa" />}
                    type="info"
                    style={{
                      marginTop: '8px',
                      marginBottom: '12px',
                      padding: '0 2px',
                    }}
                  />
                  <ul className={styles.basic_info}>
                    <li className={styles.basic_info_item}>
                      <label className={styles.label}>类型名称:</label>
                      <div className={styles.value}>
                        <Select
                          value={typeValue}
                          style={{ width: '100%' }}
                          options={typeList}
                          onChange={getElementDetail}
                        />
                      </div>
                    </li>
                  </ul>
                  {(elementDetail || []).map((element, index: number) => {
                    return (
                      <div key={index}>
                        <p className={styles.right_title2}>{element.category}</p>
                        <Descriptions title="" bordered column={1}>
                          {(element.propsInfoDtoList || [])?.map(
                            (item: ELEMENT.elementDetail, i: number) => (
                              <Descriptions.Item
                                label={`${item?.displayName} ${
                                  item.unit ? '(' + item.unit + ')' : ''
                                }`}
                                key={i}
                              >
                                {item?.value}
                              </Descriptions.Item>
                            ),
                          )}
                        </Descriptions>
                      </div>
                    );
                  })}
                </Collapse.Panel>
              ) : null}
            </Collapse>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ViewElement);
